// middleware/mobile-auth.global.ts
import { useAuthStore } from '~/stores/useAuthStore';
import { applyLoginSuccess } from '~/composables/useLoginSuccess';
import { getActivePinia } from 'pinia';

// RN 이 injectedJavaScriptBeforeContentLoaded 로 심어주는 인증 정보 대기 시간
const NATIVE_AUTH_WAIT_MS = 3000;
const NATIVE_AUTH_POLL_MS = 100;

// 주입 지연은 WebView 최초 로드 시에만 발생하므로 대기는 페이지 로드당 첫 진입에서만 한다.
// (토큰이 끝내 없거나 로그인이 실제로 실패하는 경우 매 화면 이동마다 지연되지 않도록)
let nativeAuthWaitConsumed = false;

type NativeAuth = {
  authorization: string | null;
  compId: string | null;
  userId: string | null;
};

const readNativeAuth = (): NativeAuth => ({
  authorization: localStorage.getItem('Authorization'),
  compId: localStorage.getItem('compId'),
  userId: localStorage.getItem('userId'),
});

const hasNativeAuth = (auth: NativeAuth) => !!(auth.authorization && auth.compId && auth.userId);

// window.ReactNativeWebView 는 react-native-webview 가 네이티브 레벨에서 주입하므로
// injectedJavaScriptBeforeContentLoaded(localStorage, isNativeApp) 보다 먼저 존재한다.
const isInsideNativeWebView = () => !!window.ReactNativeWebView || (window as any).isNativeApp === true;

/**
 * WebView 최초 로드 시 Android 등에서 injectedJavaScriptBeforeContentLoaded 가
 * 페이지 스크립트보다 늦게 실행되는 경우가 있어, 조건을 만족할 때까지 localStorage 를 재확인한다.
 */
const waitForNativeAuth = async (until: (auth: NativeAuth) => boolean): Promise<NativeAuth> => {
  let auth = readNativeAuth();
  const deadline = Date.now() + NATIVE_AUTH_WAIT_MS;
  while (!until(auth) && Date.now() < deadline) {
    await new Promise(resolve => setTimeout(resolve, NATIVE_AUTH_POLL_MS));
    auth = readNativeAuth();
  }
  return auth;
};

export default defineNuxtRouteMiddleware(async to => {
  // 모바일 페이지가 아닌 경우 미들웨어 건너뛰기
  if (!to.path.startsWith('/mobile')) {
    return;
  }

  // 로그인 페이지, 안내 페이지는 인증 체크 제외
  if (
    to.path === '/mobile/login' ||
    to.path === '/mobile/common/login' ||
    to.path === '/mobile/common/auth-required'
  ) {
    return;
  }

  // ⭐ Pinia가 아직 초기화되지 않았으면 스킵 (첫 로드 시)
  if (!getActivePinia()) {
    return;
  }

  // 클라이언트 사이드에서만 실행
  if (process.client) {
    const authStore = useAuthStore();

    // localStorage에서 인증 정보 먼저 확인 (네이티브 앱 판별용)
    let nativeAuth = readNativeAuth();
    const canWaitNativeAuth = !nativeAuthWaitConsumed && isInsideNativeWebView();
    nativeAuthWaitConsumed = true;

    // RN WebView 인데 아직 인증 정보가 주입되지 않았으면 잠시 대기 (WebView 최초 로드 시)
    if (!hasNativeAuth(nativeAuth) && canWaitNativeAuth) {
      console.log('⏳ [Middleware] 네이티브 인증 정보 주입 대기');
      nativeAuth = await waitForNativeAuth(hasNativeAuth);
    }

    const { authorization, compId, userId } = nativeAuth;

    // 네이티브 앱 환경인지 확인 (localStorage에 인증 정보가 있거나 window.isNativeApp이 true)
    const hasNativeAppAuth = hasNativeAuth(nativeAuth);
    const isNativeApp = hasNativeAppAuth || isInsideNativeWebView();

    console.log('📱 인증 체크 시작:', {
      path: to.path,
      isNativeApp,
      hasNativeAppAuth,
      hasAuthorization: !!authorization,
      hasCompId: !!compId,
      hasUserId: !!userId,
      isLoggedIn: authStore.isLoggedIn,
    });

    // 네이티브 앱 환경: 항상 세션 유효성 확인 후 필요시 재인증
    if (hasNativeAppAuth) {
      console.log('📱 [Middleware] 네이티브 앱 환경 - 세션 유효성 확인');

      // 1단계: /auth/me로 현재 세션이 유효한지 확인
      try {
        await authStore.checkAuth();

        // 세션은 유효하지만 사용자가 다른 경우 (다른 계정으로 로그인)
        if (authStore.user?.userId !== userId) {
          console.log('🔄 [Middleware] 사용자 불일치 - 재인증 필요', {
            sessionUserId: authStore.user?.userId,
            nativeUserId: userId,
          });
          throw new Error('User mismatch');
        }

        console.log('✅ [Middleware] 세션 유효 확인됨', { user: authStore.user?.userNm });
      } catch (error) {
        // 2단계: 세션 무효 또는 사용자 불일치 → 토큰으로 재인증
        console.log('📱 [Middleware] 세션 무효 - 토큰 기반 로그인 시도');
        authStore.$reset();

        const loginWithNativeToken = async (auth: NativeAuth): Promise<boolean> => {
          try {
            const result = await authStore.loginWithToken(auth.compId!, auth.userId!, auth.authorization!);
            if (result.success) {
              console.log('✅ [Middleware] 토큰 기반 로그인 성공');
              await applyLoginSuccess({
                menuListJson: result.menuListJson,
                user: authStore.user,
                compIconYn: authStore.compIconYn,
              });
              return true;
            }
            console.error('❌ [Middleware] 토큰 기반 로그인 실패', result.message);
          } catch (loginError) {
            console.error('❌ [Middleware] 토큰 기반 로그인 예외', loginError);
          }
          return false;
        };

        const loggedIn = await loginWithNativeToken(nativeAuth);

        // 이전 실행 때 남은 토큰으로 실패한 경우: RN 이 새 토큰을 주입하면 한 번 더 시도
        if (!loggedIn && canWaitNativeAuth) {
          const isRefreshed = (auth: NativeAuth) => hasNativeAuth(auth) && auth.authorization !== authorization;
          const refreshedAuth = await waitForNativeAuth(isRefreshed);
          if (isRefreshed(refreshedAuth)) {
            console.log('🔄 [Middleware] 새 토큰 감지 - 토큰 기반 로그인 재시도');
            await loginWithNativeToken(refreshedAuth);
          }
        }
      }
    }
    // 일반 웹 환경: 기존 로직 유지
    else if (!authStore.isLoggedIn || !authStore.user) {
      console.log('📱 [Middleware] 웹 환경 - 세션 체크');
      try {
        await authStore.checkAuth();
        console.log('✅ [Middleware] 세션 확인됨', { user: authStore.user?.userNm });
      } catch (error) {
        console.log('❌ [Middleware] 세션 없음');
      }
    }

    // 4단계: 최종 인증 상태 확인 - 위의 모든 시도 후에도 인증 안되어 있으면 리다이렉트
    console.log('📱 [Middleware] 4단계: 최종 인증 상태 확인', {
      isLoggedIn: authStore.isLoggedIn,
      hasUser: !!authStore.user,
      user: authStore.user,
    });

    // 5단계: 비밀번호 변경 필요 여부 체크
    if (authStore.isLoggedIn && authStore.user) {
      // 비밀번호 변경 완료/연장 상태인 경우 스킵 (세션 동안 유지)
      const passwordChangeCompleted = sessionStorage.getItem('passwordChangeCompleted');
      if (passwordChangeCompleted === 'true') {
        // 플래그를 제거하지 않고 세션 동안 유지하여 새로고침해도 스킵
        authStore.user.pwInitYn = 'N';
        authStore.user.alertPwChangeYn = 'N';
        console.log('🔐 [Middleware] 비밀번호 변경 완료/연장 상태 - 스킵');
      }
      // 비밀번호 변경이 필요한 경우 (이미 비밀번호 변경 페이지에 있으면 스킵)
      else if (authStore.user.pwInitYn === 'Y' || authStore.user.alertPwChangeYn === 'Y') {
        if (to.path === '/mobile/password-change') {
          console.log('🔐 [Middleware] 이미 비밀번호 변경 페이지에 있음 - 스킵');
        } else {
          console.log('🔐 [Middleware] 비밀번호 변경 필요 - 리다이렉트', {
            pwInitYn: authStore.user.pwInitYn,
            alertPwChangeYn: authStore.user.alertPwChangeYn,
          });
          // 현재 페이지를 저장 (비밀번호 변경 후 복귀용)
          sessionStorage.setItem('passwordChangeRedirectPath', to.path);
          // 비밀번호 변경 페이지로 리다이렉트
          return navigateTo('/mobile/password-change');
        }
      }
    }

    console.log('✅ [Middleware] 인증 성공 → 페이지 진입:', {
      user: authStore.user?.userNm,
      userId: authStore.user?.userId,
      path: to.path,
    });
  }
});
